/**
 * Minimal structural shape needed to group projects by their parent space.
 * The codebase has two `ProjectDocument` declarations (one in
 * `lib/db/rxdb/hooks/entities/projects` and one in `lib/db/rxdb/schemas/project`)
 * that have drifted; this generic accepts either via structural typing.
 */
type ProjectLike = {
  id: string
  isDeleted: boolean
  parentProjectId: string | null
}

export type ProjectGroup<T extends ProjectLike> = {
  space: T | null
  children: Array<T>
}

export function groupProjectsBySpace<T extends ProjectLike>(
  projects: Array<T> | null | undefined,
): Array<ProjectGroup<T>> {
  if (!projects || projects.length === 0) return []

  const active = projects.filter((p) => !p.isDeleted)
  const byId = new Map(active.map((p) => [p.id, p]))

  const spaces = active.filter((p) => !p.parentProjectId)
  const groups: Array<ProjectGroup<T>> = []

  for (const space of spaces) {
    const children = active.filter((p) => p.parentProjectId === space.id)
    if (children.length === 0) continue
    groups.push({ space, children })
  }

  const orphans = active.filter(
    (p) => !!p.parentProjectId && !byId.has(p.parentProjectId),
  )
  if (orphans.length > 0) {
    groups.push({ space: null, children: orphans })
  }

  return groups
}
